import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from "vue"
import AppPage from "@/components/layout/AppPage.vue"
import { THEMES } from "@/constants"
import SearchInput from "@/components/widgets/common/SearchInput.vue"
import StripeCardInput from "@/components/widgets/input/StripeCardInput.vue"
import Icon from "@/components/widgets/icons/Icon.vue"
import { useQuery } from "@vue/apollo-composable"
import {
  ClientSubscriptionsQueryQuery,
  ClientSubscriptionsQuery,
  SubscriptionPeriodTypeEnum,
  Subscription,
  InviteSubscriptionUserMutationMutation,
  InviteSubscriptionUserMutation,
  InviteSubscriptionUserMutationMutationVariables,
  UpdatePaymentMethodMutation,
  UpdatePaymentMethodMutationMutation,
  UpdatePaymentMethodMutationMutationVariables,
  ResendInvitationMutationMutation,
  ResendInvitationMutationMutationVariables,
  ResendInvitationMutation,
  RevokeInvitationMutationMutation,
  RevokeInvitationMutationMutationVariables,
  RevokeInvitationMutation,
  SubscriptionUser,
} from "@/gql"
import { useAuth } from "@/store/auth/useAuth"
import { useUtilities } from "@/hooks/useUtilities"
import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import { useSearch } from "@/hooks/useSearch"


export default /*#__PURE__*/_defineComponent({
  __name: 'Home',
  setup(__props) {

const theme = {
  primaryColor: THEMES.AHEAD_PRIMARY,
  textColor: THEMES.WHITE_TEXT_COLOR,
  iconColor: THEMES.AHEAD_PRIMARY_COLOR,
}

const { user } = useAuth()
const { formatDate } = useUtilities()
const { mutate } = useMutation()
const { addSuccess, addError } = useNotification()

const searchKey = ref("")
const showAddMember = ref(false)
const showUpgradePaymentMethodModal = ref(false)
const showConfirmDialog = ref(false)
const subscription = ref<Subscription | null>(null)
const cachedSubscriptionUsers = ref<SubscriptionUser[] | null>(null)
const isSubmitting = ref(false)
const isSubmittingCardInfo = ref(false)

const invitations = computed(() => {
  if (!subscription.value) return []
  return subscription.value?.invitations.filter((i) => i.state === "pending")
})

const form = ref({
  email: "",
  name: "",
})

const { loading, result, onResult } = useQuery<ClientSubscriptionsQueryQuery>(
  ClientSubscriptionsQuery,
  {
    id: user && user.clientsAsAdmin && user?.clientsAsAdmin[0].id,
  }
)

onResult((result) => {
  if (result?.data) {
    subscription.value = result.data.myClient?.subscriptions[0] as Subscription
    cachedSubscriptionUsers.value = result.data.myClient?.subscriptions[0]
      ?.subscriptionUsers as SubscriptionUser[]
  }
})

watch(searchKey, (searchKey) => {
  if (!subscription.value || !cachedSubscriptionUsers.value?.length) return

  if (!searchKey) {
    subscription.value.subscriptionUsers = cachedSubscriptionUsers.value as SubscriptionUser[]
  } else {
    subscription.value.subscriptionUsers = searchDocuments(searchKey) as SubscriptionUser[]
  }
})

const searchDocuments = (searchTerm: string) => {
  if (!cachedSubscriptionUsers.value) return []
  return useSearch({
    items: cachedSubscriptionUsers.value,
    search: searchTerm,
    fields: ["name", "email"],
  })
}

const showSearchInput = computed(() => {
  const hasMembers = cachedSubscriptionUsers.value && cachedSubscriptionUsers.value?.length > 0
  const hasKey = searchKey.value.length > 0
  return hasMembers || hasKey
})

const refetchQueries = computed(() => {
  return [
    {
      query: ClientSubscriptionsQuery,
      variables: {
        id: user && user.clientsAsAdmin && user?.clientsAsAdmin[0].prn,
      },
    },
  ]
})

const onInviteUser = async () => {
  if (!subscription.value?.prn || !form.value.email) return
  isSubmitting.value = true

  // Mutate
  const result = await mutate<
    InviteSubscriptionUserMutationMutation,
    InviteSubscriptionUserMutationMutationVariables
  >({
    mutation: InviteSubscriptionUserMutation,
    variables: {
      email: form.value.email,
      name: form.value.name,
      subscriptionId: subscription.value.prn,
    },
    handlePayloadErrors: true,
    refetchQueries: refetchQueries.value,
  })
  // Done
  if (result?.data?.inviteSubscriptionUser.subscriptionUser?.id) {
    addSuccess(`Member has been invited`)
    showAddMember.value = false
    form.value.email = ""
  }
  isSubmitting.value = false
}

const observer = ref(null)

const onSubmitInvitation = async () => {
  const validity = await (observer.value as any)?.validateWithInfo()

  if (!validity.isValid) return
  onInviteUser()
}

const onUpdatePaymentMethod = async (card: any) => {
  if (!subscription.value?.paymentMethod?.id) return

  isSubmittingCardInfo.value = true

  const result = await mutate<
    UpdatePaymentMethodMutationMutation,
    UpdatePaymentMethodMutationMutationVariables
  >({
    mutation: UpdatePaymentMethodMutation,
    variables: {
      paymentMethodId: subscription.value.paymentMethod.id,
      paymentMethodInput: {
        stripeCardId: card.token.card.id,
        paymentToken: card.token.id,
        lastFour: card.token.card.last4,
        name: card.token.card.name,
        cardType: card.token.card.brand,
      },
    },
    handlePayloadErrors: true,
  })

  if (result?.data?.updatePaymentMethod.paymentMethod?.id) {
    addSuccess("Payment method updated successfully")
    showUpgradePaymentMethodModal.value = false
  }
  isSubmitting.value = false
}

const inviteActions = {
  RESEND: "resend",
  REVOKE: "revoke",
} as const

const inviteAction = ref<(typeof inviteActions)[keyof typeof inviteActions] | null>(null)

const invitee = ref({
  email: "",
  prn: "",
})

const onShowInvitesDialog = (
  action: (typeof inviteActions)[keyof typeof inviteActions],
  data: { email: string; prn: string }
) => {
  inviteAction.value = action
  invitee.value = data
  showConfirmDialog.value = true
}

const confirm = async () => {
  switch (inviteAction.value) {
    case inviteActions.RESEND:
      resendInvite()
      break

    case inviteActions.REVOKE:
      revokeInvite()
      break

    default:
      addError(`Unknown action: ${inviteAction.value}`)
      break
  }
}

const resendInvite = async () => {
  if (!invitee.value.prn) return
  isSubmitting.value = true

  const result = await mutate<
    ResendInvitationMutationMutation,
    ResendInvitationMutationMutationVariables
  >({
    mutation: ResendInvitationMutation,
    variables: {
      id: invitee.value.prn,
    },
    handlePayloadErrors: true,
  })

  if (!result?.data?.resendInvitation.error) {
    addSuccess(`Member has been invited again`)
    showConfirmDialog.value = false
  }
  isSubmitting.value = false
}
const revokeInvite = async () => {
  if (!invitee.value.prn) return
  isSubmitting.value = true

  const result = await mutate<
    RevokeInvitationMutationMutation,
    RevokeInvitationMutationMutationVariables
  >({
    mutation: RevokeInvitationMutation,
    variables: {
      id: invitee.value.prn,
    },
    handlePayloadErrors: true,
    refetchQueries: refetchQueries.value,
  })

  if (!result?.data?.revokeInvitation.error) {
    addSuccess(`Member has been removed`)
    showConfirmDialog.value = false
  }
  isSubmitting.value = false
}

return { __sfc: true,theme, user, formatDate, mutate, addSuccess, addError, searchKey, showAddMember, showUpgradePaymentMethodModal, showConfirmDialog, subscription, cachedSubscriptionUsers, isSubmitting, isSubmittingCardInfo, invitations, form, loading, result, onResult, searchDocuments, showSearchInput, refetchQueries, onInviteUser, observer, onSubmitInvitation, onUpdatePaymentMethod, inviteActions, inviteAction, invitee, onShowInvitesDialog, confirm, resendInvite, revokeInvite, AppPage, THEMES, SearchInput, StripeCardInput, Icon, SubscriptionPeriodTypeEnum }
}

})